/**
 * Identify DOM elements robustly for storage in our DB.
 * Generates a CSS selector, an XPath, and a content fingerprint.
 *
 * Shared by the scanners (extension, Browser Run bundle) that WRITE these ids
 * and the widget loader that RESOLVES them at render time — selector → xpath →
 * content fingerprint — so both sides compute the fingerprint identically.
 * Browser-only (DOM); imported via '@nagisot/shared/element-id', never from
 * the package root, so the Worker API doesn't pull DOM types.
 */

/** Build a stable CSS selector for an element. */
export function cssSelector(el: Element): string {
  if (el.id) return `#${cssEscape(el.id)}`;
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur.nodeType === 1 && cur.tagName !== 'HTML' && parts.length < 6) {
    let part = cur.tagName.toLowerCase();
    if (cur.id) {
      parts.unshift(`#${cssEscape(cur.id)}`);
      break;
    }
    const className = (cur.getAttribute('class') || '').trim().split(/\s+/).filter(Boolean).slice(0, 2);
    if (className.length) part += '.' + className.map(cssEscape).join('.');
    if (cur.parentElement) {
      const sameTag = Array.from(cur.parentElement.children).filter((c) => c.tagName === cur!.tagName);
      if (sameTag.length > 1) {
        const idx = sameTag.indexOf(cur as Element) + 1;
        part += `:nth-of-type(${idx})`;
      }
    }
    parts.unshift(part);
    cur = cur.parentElement;
  }
  return parts.join(' > ');
}

/** Build XPath for an element. */
export function xpath(el: Element): string {
  const parts: string[] = [];
  let cur: Element | null = el;
  while (cur && cur.nodeType === 1) {
    if (cur.id) {
      parts.unshift(`//*[@id="${cur.id}"]`);
      return parts.join('/');
    }
    let idx = 1;
    let sib = cur.previousElementSibling;
    while (sib) {
      if (sib.tagName === cur.tagName) idx++;
      sib = sib.previousElementSibling;
    }
    parts.unshift(`${cur.tagName.toLowerCase()}[${idx}]`);
    cur = cur.parentElement;
  }
  return '/' + parts.join('/');
}

/** Fingerprint an element for content-based re-identification. */
export async function contentHash(el: Element): Promise<string> {
  const features = describeElement(el);
  return sha256(features);
}

/** Synchronous version using simple hash (less collision-resistant but faster). */
export function contentHashSync(el: Element): string {
  return djb2(describeElement(el));
}

/** Every fingerprint this element could have had at scan time. An <img>'s
 *  fingerprint depends on which URL the browser picked (currentSrc vs. src vs.
 *  a srcset candidate for another viewport), so all of them are accepted. */
export function contentHashCandidates(el: Element): string[] {
  const out = new Set<string>([contentHashSync(el)]);
  if (el.tagName === 'IMG') {
    const img = el as HTMLImageElement;
    const urls = [img.currentSrc, img.getAttribute('src') || '',
      ...(img.getAttribute('srcset') || '').split(',').map((c) => c.trim().split(/\s+/)[0] || '')];
    for (const u of urls) if (u) out.add(djb2(`img|src:${canonicalSrc(u)}`));
  }
  return [...out];
}

function describeElement(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const parts: string[] = [tag];
  if (tag === 'img') {
    const img = el as HTMLImageElement;
    parts.push('src:' + canonicalSrc(img.currentSrc || img.src || ''));
  } else if (tag === 'a') {
    const a = el as HTMLAnchorElement;
    parts.push('href:' + (a.href || ''));
    parts.push('text:' + (a.textContent || '').trim().slice(0, 60));
  } else if (tag === 'input' || tag === 'textarea' || tag === 'select') {
    const inp = el as HTMLInputElement;
    parts.push('name:' + (inp.name || ''));
    parts.push('type:' + (inp.type || ''));
    parts.push('placeholder:' + (inp.placeholder || ''));
  } else {
    parts.push('text:' + (el.textContent || '').trim().slice(0, 100));
  }
  return parts.join('|');
}

function canonicalSrc(s: string): string {
  try {
    const u = new URL(s, location.href);
    return u.origin + u.pathname;
  } catch {
    return s;
  }
}

function cssEscape(s: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') return CSS.escape(s);
  return s.replace(/(["\\.#>+~*:[\]()])/g, '\\$1');
}

function djb2(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(16);
}

async function sha256(s: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(s));
  return [...new Uint8Array(buf)].map((b) => b.toString(16).padStart(2, '0')).join('');
}
